<template>
  <div>
    <v-app-bar app color="white" dark height="56" elevation="0" class="app-bar">
      <v-container fluid height="56" class="pt-0">
        <div class="app-bar--content">
          <div class="d-flex align-center text-no-wrap">
            <button-toggle-group
              v-if="hasMspAccess"
              :model-value="selectedWorkspaceView"
              :items="workspaceNavigatorItems"
              @update:model-value="handleWorkspaceViewChange"
              density="compact"
            />
            <span
              v-else
              data-testid="app-bar-app-title"
              class="subtitle2 text-primary text-uppercase"
              v-text="appTitle"
            />
          </div>
          <div v-if="logged" class="d-flex justify-end w-100">
            <div class="d-inline-flex align-center" data-testid="app-bar-select-workspace-dropdown">
              <select-workspace-hierarchy-dropdown
                data-testid="app-bar-select-workspace-hierarchy-dropdown"
                v-if="isMspRoute"
              />
              <template v-else>
                <trial-countdown-notice data-testid="app-bar-trial-countdown-notice" />
                <select-workspace-dropdown
                  data-testid="app-bar-select-workspace-dropdown"
                  :enable-dropdown="hasMultipleWorkspaces"
                />
              </template>
              <ai-assistant class="ml-3"></ai-assistant>
            </div>
          </div>
        </div>
      </v-container>
    </v-app-bar>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent } from "vue";
import { useAccountStore } from "@/_store/account.module";
import { storeToRefs } from "pinia";
import SelectWorkspaceDropdown from "@/components/modals/SelectWorkspaceDropdown.vue";
import SelectWorkspaceHierarchyDropdown from "@/components/modals/SelectWorkspaceHierarchyDropdown.vue";
import { RouteName } from "@/constants/routes.ts";
import { useRouter } from "vue-router";
import {
  WorkspaceAction,
  WorkspaceNavigationButton,
  WorkspaceType,
} from "@/constants/workplaces.ts";
import TrialCountdownNotice from "@/components/TrialCountdownNotice.vue";
import { componentDialogsConfigConstructor, trackGtmEvent } from "@/_helpers/utils.ts";
import { type MspHierarchyItem } from "@/_store/msp/msp.module.ts";
import { GtmAction, GTMParams } from "@/constants/gtm.ts";
import { useI18n } from "vue-i18n";
import { useDialogsStore } from "@/_store/dialogs.module.ts";
import { ModalWidth } from "@/constants/modals.ts";
import SelectHierarchyModal from "@/components/modals/SelectHierarchyModal.vue";
import { useAppsManagementModule } from "@/_store/apps-mgmt.module.ts";
import { useWorkspaceStore } from "@/_store/workspace.module.ts";
import AiAssistant from "@/components/ai-assistant/AiAssistant.vue";
import ButtonToggleGroup, { type ButtonToggleItem } from "@/components/ButtonToggleGroup.vue";

export default defineComponent({
  components: {
    AiAssistant,
    TrialCountdownNotice,
    SelectWorkspaceHierarchyDropdown,
    SelectWorkspaceDropdown,
    ButtonToggleGroup,
  },
  props: {
    appTitle: {
      type: String,
      required: true,
    },
    isMspRoute: {
      type: Boolean,
      required: true,
    },
  },
  setup(props) {
    const appsMgmtStore = useAppsManagementModule();
    const accountStore = useAccountStore();
    const dialogsStore = useDialogsStore();
    const workspaceStore = useWorkspaceStore();
    const { hasMultipleWorkspaces, logged, hasMspAccess } = storeToRefs(accountStore);
    const router = useRouter();
    const i18n = useI18n();

    const workspaceRoutes: Record<WorkspaceNavigationButton, RouteName> = {
      [WorkspaceNavigationButton.GLOBAL]: RouteName.MSP_DASHBOARD_PAGE,
      [WorkspaceNavigationButton.WORKSPACE]: RouteName.DASHBOARD_PAGE,
    };

    const workspaceNavigatorItems = computed<ButtonToggleItem<WorkspaceNavigationButton>[]>(() => [
      {
        value: WorkspaceNavigationButton.GLOBAL,
        label: i18n.t(`appBar.${WorkspaceNavigationButton.GLOBAL}`),
      },
      {
        value: WorkspaceNavigationButton.WORKSPACE,
        label: i18n.t(`appBar.${WorkspaceNavigationButton.WORKSPACE}`),
      },
    ]);

    const selectedWorkspaceView = computed(() => {
      return props.isMspRoute
        ? WorkspaceNavigationButton.GLOBAL
        : WorkspaceNavigationButton.WORKSPACE;
    });

    const showHierarchySelectModal = computed(() => {
      return (
        !appsMgmtStore.mspWorkspaceId && workspaceStore.workspace.type === WorkspaceType.REGULAR
      );
    });

    const handleWorkspaceViewChange = async (value: WorkspaceNavigationButton) => {
      const route = workspaceRoutes[value];
      if (!route) {
        console.error(`No route found for workspace navigation: ${value}`);
        return;
      }

      trackGtmEvent(GtmAction.APP_BAR, i18n.t(`appBar.${value}`));
      if (value === WorkspaceNavigationButton.GLOBAL && showHierarchySelectModal.value) {
        dialogsStore.openDialog({
          ...componentDialogsConfigConstructor({
            action: WorkspaceAction.NAVIGATE_TO_GLOBAL_VIEW,
            callback: () => {},
            item: {
              closeCallback: async (workspace: MspHierarchyItem) => {
                appsMgmtStore.$patch({
                  mspWorkspaceId: workspace.workspaceId,
                });
                await router.push({ name: route });
                trackGtmEvent(
                  GtmAction.MSP_WORKSPACE_SELECTOR,
                  GTMParams.MSP_MODAL_WORKSPACE_SELECTED
                );
                dialogsStore.closeDialog();
              },
            },
            component: SelectHierarchyModal,
            width: ModalWidth.LARGE,
            disable: false,
          }),
          scrollable: false,
          disablePersistentAnimation: true,
          footer: null,
        });
      } else {
        await router.push({ name: route });
      }
    };

    return {
      hasMultipleWorkspaces,
      logged,
      hasMspAccess,
      RouteName,
      workspaceNavigatorItems,
      selectedWorkspaceView,
      handleWorkspaceViewChange,
    };
  },
});
</script>

<style lang="scss" scoped>
.app-bar {
  box-shadow: 0 4px 16px 0 rgba(0, 0, 0, 0.12) !important;

  &--content {
    max-width: 1200px;
    width: 100%;
    margin: auto;
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 56px;
  }
}
</style>
